import { ref } from 'vue';
import { useClientAnalytics } from '@ilteducation/client-analytics';
import { Tabs } from '@ilteducation/videos';
import { videoSessionId } from '@/composables/use-video-session';

// One event name for the whole surface, with `tab` (where) and `action` (what) as separate
// dimensions. A new tab or action is a new key here, never a new event name.
export const TAB_KEYS = {
  [Tabs.keywords]: 'keywords',
  [Tabs.quiz]: 'quiz',
  [Tabs.exercises]: 'exercises',
  [Tabs.subtitles]: 'subtitles',
  [Tabs.articles]: 'articles',
  [Tabs.immerse]: 'immerse',
  [Tabs.curriculum]: 'curriculum',
  [Tabs.materials]: 'materials',
  [Tabs.videosInArea]: 'videos_in_area',
} as const;

type VideoTabKey = (typeof TAB_KEYS)[Tabs];

export type VideoTabAction =
  | 'tab_opened'
  | 'open_flashcards'
  | 'print'
  | 'select_translation_language'
  | 'clear_translation_language'
  | 'open_transcript'
  | 'open_easy_to_read';

/**
 * Set by the film tabs so the individual tab components can report their sub-actions without the
 * video context being threaded through their props. Unset until they mount, which makes
 * trackTabAction a no-op for components that are also rendered outside the video page.
 */
const videoContext = ref<{ videoId: string; categoryId?: string; areaId?: string }>();

export function setVideoTabContext(context: { videoId: string; categoryId?: string; areaId?: string }) {
  videoContext.value = context;
}

export function trackTabAction(tab: VideoTabKey, action: VideoTabAction, target?: string): void {
  if (!videoContext.value) {
    return;
  }

  useClientAnalytics().sendAnalyticsEvent('AppUserEventType', {
    eventName: 'video_tab_interaction',
    metadata: {
      service: 'manabua',
      ...videoContext.value,
      videoSessionId: videoSessionId.value,
      tab,
      action,
      target,
    },
  });
}
